import { FormControl, InputLabel, Select } from "@mui/material";
import React, { useCallback, type ReactNode } from "react";

export type SelectInfiniteScrollConfig = {
  hasNextPage: boolean;
  onLoadMore: () => void;
  isFetchingNextPage?: boolean;
};

interface SelectStyledProps {
  id: string;
  name: string;
  label: string;
  value: any;
  onChange: (e: any) => void;
  children: React.ReactNode;
  disabled?: boolean;
  /** Scroll infinito na lista do menu (ex.: paginação via React Query). */
  infiniteScroll?: SelectInfiniteScrollConfig;
  /** Altura máxima da lista rolável do menu (px). */
  menuListMaxHeight?: number;
  multiple?: boolean;
  renderValue?: (selected: unknown) => ReactNode;
}

const SCROLL_LOAD_THRESHOLD_PX = 28;

const SelectStyled: React.FC<SelectStyledProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  children,
  disabled = false,
  infiniteScroll,
  menuListMaxHeight = 280,
  multiple = false,
  renderValue,
}) => {
  const resolvedValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : (value ?? "");
  const handleMenuListScroll = useCallback(
    (e: React.UIEvent<HTMLUListElement>) => {
      if (!infiniteScroll) return;
      const { hasNextPage, onLoadMore, isFetchingNextPage } = infiniteScroll;
      if (!hasNextPage || isFetchingNextPage) return;
      const el = e.currentTarget;
      if (
        el.scrollHeight - el.scrollTop <=
        el.clientHeight + SCROLL_LOAD_THRESHOLD_PX
      ) {
        void onLoadMore();
      }
    },
    [infiniteScroll],
  );

  return (
    <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
      <InputLabel
        sx={{
          color: "#666",
          "&.Mui-focused": { color: "#76A6C2" },
        }}
      >
        {label}
      </InputLabel>
      <Select
        id={id}
        name={name}
        multiple={multiple}
        displayEmpty={multiple}
        value={resolvedValue}
        label={label}
        onChange={onChange}
        disabled={disabled}
        renderValue={renderValue}
        MenuProps={{
          PaperProps: {
            sx: {
              bgcolor: "#f1f1f1",
              maxHeight: menuListMaxHeight + 16,
              "& .MuiMenuItem-root": {
                color: "#000",
                "&.Mui-selected": {
                  backgroundColor: "#76A6C2 !important",
                  color: "#fff",
                },
                "&:hover": {
                  backgroundColor: "#bff3ed",
                },
              },
            },
          },
          MenuListProps: {
            sx: {
              maxHeight: menuListMaxHeight,
              overflowY: "auto",
            },
            ...(infiniteScroll
              ? { onScroll: handleMenuListScroll }
              : {}),
          },
        }}
        sx={{
          backgroundColor: "#f1f1f1",
          height: "40px",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#76A6C2" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#76A6C2",
          },
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            height: "40px",
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: "30px",
            color: "#000",
          },
        }}
      >
        {children}
      </Select>
    </FormControl>
  );
};

export default SelectStyled;
